/**
 * PURPOSE: Clasificación final de UNA prueba (`PruebaDetail.standings`), tal como la calcula el servidor.
 * RESPONSIBILITIES: Puesto (con empate), participante, universidad, marca y viento, puntos y medalla.
 *   Se usa en el detalle y en la vista previa de "Oficializar". Con `ranking` muestra `PruebaDetail.ranking`,
 *   sin puntos ni medalla: en carreras, mejor tiempo y ronda en que lo hizo; en concursos con corte a los 8
 *   mejores (filas sin `fromRound`), mejor marca y si fue finalista.
 * COLLABORATORS: PruebaDetailPanel.jsx, OfficializeModal.jsx.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/PruebaStandingsTable.jsx
 */
import React from "react";

import { institutionAbrev, participantName } from "../labels.js";
import { FrozenScroll, buildFrozenColumns } from "./grids/gridCells.jsx";
import { EntryStatusChip, MedalChip } from "./ui.jsx";

const th = "whitespace-nowrap px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500";
const thc = "whitespace-nowrap px-3 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-500";

const frozen = buildFrozenColumns([
  ["place", 4],
  ["participant", 12],
  ["institution", 5.5],
]);

const ROUND_LABELS = { heat: "Serie", final: "Final", single: "Serie única" };

const PruebaStandingsTable = ({
  standings,
  emptyLabel = "Todavía no hay clasificación para esta prueba.",
  ranking = false,
}) => {
  const rows = Array.isArray(standings) ? standings : [];
  if (!rows.length) return <p className="px-4 py-4 text-sm italic text-slate-500">{emptyLabel}</p>;
  const fieldRanking = ranking && rows.every((row) => !row.fromRound);

  return (
    <FrozenScroll>
      <table className="w-full min-w-[560px] table-auto">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th {...frozen("place", thc)}>Puesto</th>
            <th {...frozen("participant", th)}>Participante</th>
            <th {...frozen("institution", th)}>Univ.</th>
            <th className={thc}>{ranking ? (fieldRanking ? "Mejor marca" : "Mejor tiempo") : "Marca"}</th>
            {ranking ? (
              <th className={thc}>{fieldRanking ? "Final" : "Ronda"}</th>
            ) : (
              <>
                <th className={thc}>Puntos</th>
                <th className={thc}>Medalla</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.participant?.entryId ?? row.participant?.teamId ?? index}-${index}`}
              className="border-b border-slate-100 bg-white last:border-b-0"
            >
              <td {...frozen("place", "px-3 py-2 text-center text-sm font-black text-slate-800")}>
                {row.place ?? "—"}
                {row.tied ? <span className="ml-0.5 text-xs text-slate-400" title="Empate">=</span> : null}
              </td>
              <td {...frozen("participant", "px-3 py-2 text-sm font-semibold text-slate-800 [overflow-wrap:anywhere]")}>{participantName(row.participant)}</td>
              <td {...frozen("institution", "px-3 py-2 text-xs font-bold text-slate-600")} title={row.participant?.institution?.name}>
                {institutionAbrev(row.participant?.institution)}
              </td>
              <td className="px-3 py-2 text-center">
                {row.mark ? (
                  <span className="font-mono text-sm font-bold text-slate-900">{row.mark}</span>
                ) : (
                  <EntryStatusChip status={row.status} />
                )}
                {row.mark && row.status && row.status !== "OK" ? (
                  <span className="ml-1">
                    <EntryStatusChip status={row.status} />
                  </span>
                ) : null}
                {row.wind ? <span className="ml-1 text-[11px] text-slate-500">({row.wind})</span> : null}
              </td>
              {ranking ? (
                <td className="px-3 py-2 text-center text-xs font-semibold text-slate-500">
                  {fieldRanking ? (
                    row.finalist ? (
                      <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700">Finalista</span>
                    ) : (
                      "—"
                    )
                  ) : (
                    ROUND_LABELS[row.fromRound] || "—"
                  )}
                </td>
              ) : (
                <>
                  <td className="px-3 py-2 text-center text-sm font-bold text-indigo-700">{row.points ?? "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <MedalChip medal={row.medal} />
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </FrozenScroll>
  );
};

export default PruebaStandingsTable;
